import { Box, Button, Skeleton, Stack, Tab, Tabs } from "@mui/material";
import useTabChanges from "customHooks/useTabChanges";
import { Suspense, lazy, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import PublishIcon from "@mui/icons-material/Publish";
import LeftSectionLayout from "components/layout/twoSectionLayout/LeftSectionLayout";
import MainLayoutSection from "components/layout/twoSectionLayout/MainLayoutSection";
import RightSectionLayout from "components/layout/twoSectionLayout/RightSectionLayout";
import SupplierLeft from "components/widgets/phase-3/supplier/left/SupplierLeft";

import { useSelector } from "react-redux";
import apiBolerplate from "utils/_helpers/apiBolerplate";
import { basicDataFirstTab, basicDataSecondTab } from "./leftSectionData";
import { companySetup, dataSetup } from "./riskSectionData";
const KPIData = lazy(() => import("./KPIData"));
const EsgInsights = lazy(() => import("./EsgInsights"));
const DigitalReputation = lazy(() => import("./DigitalReputation"));
const EsgProfile = lazy(() => import("./EsgProfile"));

export const customScrollbar = {
  "&::-webkit-scrollbar": { width: 10, height: 10 },
  "&::-webkit-scrollbar-track": { backgroundColor: "#fbfbfb" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#e9e9e9",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#bbbbbb" },
};

export const fallback = ({ showFooter, size } = {}) => (
  <Box p={2}>
    {showFooter && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Skeleton
          variant="rectangular"
          width={180}
          height={50}
          sx={{ borderRadius: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width={180}
          height={40}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    )}

    <Stack spacing={2}>
      {Array.from({ length: size ? 3 : 11 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={size ? 150 : 40}
          sx={{ borderRadius: 1 }}
        />
      ))}
    </Stack>

    {showFooter && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 4,
        }}
      >
        <Skeleton
          variant="rectangular"
          width={180}
          height={40}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    )}
  </Box>
);

const RiskTabs = () => {
  const navigate = useNavigate();
  const tabs = useMemo(
    () => [
      {
        label: "Company Setup",
        leftComponent: basicDataFirstTab,
        rightComponent: companySetup,
      },
      {
        label: "Data Setup",
        leftComponent: basicDataSecondTab,
        rightComponent: dataSetup,
      },
      { label: "KPI Data", rightComponent: () => <KPIData /> },
      {
        label: "Digital Reputation",
        rightComponent: () => <DigitalReputation edit={true} />,
      },
      { label: "Insights", rightComponent: () => <EsgInsights edit={true} /> },
      {
        label: "ESG Diagnosis",
        rightComponent: () => <EsgProfile />,
      },
    ],
    []
  );

  const {
    selectedTab,
    selectedSubTab,
    handleChange,
    handleSubTabChange,
    setSelectedSubTab,
    setSelectedTab,
  } = useTabChanges(tabs);

  const currentTab = tabs[selectedTab];
  const hasLeft = Boolean(currentTab?.leftComponent);

  const handleTabChanges = () => {
    const current = tabs[selectedTab];
    const totalSubs = current.leftComponent?.length || 0;
    const isLastSubTab = totalSubs ? selectedSubTab + 1 === totalSubs : true;

    if (isLastSubTab) {
      setSelectedSubTab(0);
      setSelectedTab(Math.min(selectedTab + 1, tabs.length - 1));
    } else {
      setSelectedSubTab(selectedSubTab + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedTab === 0 && selectedSubTab === 0) return;

    if (selectedSubTab > 0) {
      setSelectedSubTab(selectedSubTab - 1);
    } else {
      const prevTabIndex = selectedTab - 1;
      const prevTab = tabs[prevTabIndex];
      setSelectedTab(prevTabIndex);
      setSelectedSubTab(prevTab.leftComponent?.length - 1 || 0);
    }
  };

  const isLastTab =
    selectedTab === tabs.length - 1 &&
    (!currentTab.leftComponent ||
      selectedSubTab === currentTab.leftComponent.length - 1);

  const {
    isin,
    year,
    _id: company_id,
  } = useSelector((state) => state.riskAdminReducer.singleCompanyDetails);
  const handlePublishCompany = async () => {
    await apiBolerplate({
      url: "risk_admin/organization/publish_company",
      bodyData: { isin, year, company_id },
      messageType: "success",
    });
  };
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          aria-label="supplier tabs"
        >
          {tabs.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handlePublishCompany}
          startIcon={<PublishIcon />}
          sx={{ fontSize: "0.75rem", py: 0 }}
        >
          Publish Company
        </Button>
      </Box>
      <MainLayoutSection
        leftWidth={hasLeft ? "29%" : "0%"}
        rightWidth={hasLeft ? "69%" : "100%"}
        height={"75vh"}
        left={
          hasLeft && (
            <LeftSectionLayout>
              <SupplierLeft
                component={currentTab.leftComponent}
                selectedSubTab={selectedSubTab}
                handleSubTabChange={handleSubTabChange}
              />
            </LeftSectionLayout>
          )
        }
        right={
          <Suspense fallback={fallback({ showFooter: true })}>
            <RightSectionLayout>
              {hasLeft
                ? currentTab?.rightComponent?.[selectedSubTab]?.()
                : currentTab?.rightComponent?.()}
            </RightSectionLayout>
          </Suspense>
        }
      />
      <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
        <Button
          variant="text"
          disabled={!selectedTab && !selectedSubTab}
          onClick={handlePrevious}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          onClick={isLastTab ? () => navigate("/risk_admin") : handleTabChanges}
        >
          {isLastTab ? "Back to Listing Page" : "Proceed"}
        </Button>
      </Stack>
    </Box>
  );
};

export default RiskTabs;
